import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  AdminUserData,
  AdminUserKpis,
  RoleAssignmentPayload,
  ProvisionUserPayload,
  RolePermissionMatrixResponse,
  RoleGroupData,
  userService,
} from "../../services/userService";

export interface FetchUsersParams {
  search?: string;
  role?: string;
  status?: string;
}

/**
 * Query hook for SuperAdmin Users Directory & KPIs
 */
export const useAdminUsersQuery = (params?: FetchUsersParams) => {
  return useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: ({ signal }) => userService.getUsers({ ...params, signal }),
  });
};

/**
 * Query hook for specific user details
 */
export const useAdminUserDetailQuery = (id?: number | string) => {
  return useQuery({
    queryKey: queryKeys.users.detail(id || ""),
    queryFn: ({ signal }) => userService.getUserDetail(id!, signal),
    enabled: Boolean(id),
  });
};

/**
 * Query hook for Role & Permission Governance Matrix
 */
export const useRbacMatrixQuery = () => {
  return useQuery({
    queryKey: queryKeys.rbac.matrix(),
    queryFn: ({ signal }) => userService.getRolePermissionMatrix(signal),
  });
};

/**
 * Mutation hook to toggle active / suspended status
 */
export const useToggleUserStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: number | string; isActive: boolean }) =>
      userService.toggleStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
};

/**
 * Mutation hook to permanently delete a user
 */
export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
};

/**
 * Mutation hook to provision a new user account
 */
export const useProvisionUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ProvisionUserPayload) => userService.provisionUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
};

/**
 * Mutation hook to assign roles
 */
export const useAssignUserRolesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: RoleAssignmentPayload }) =>
      userService.assignRoles(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.rbac.all });
    },
  });
};

/**
 * Mutation hook to assign permissions and groups
 */
export const useAssignUserPermissionsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: { permissions?: string[]; groups?: string[] };
    }) => userService.assignPermissions(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
};

/**
 * Mutation hook to create a custom role group
 */
export const useCreateCustomRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; permissions: string[] }) =>
      userService.createCustomRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rbac.all });
    },
  });
};
